import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { tenantModelConfigApi, type ProviderStatus } from "@/api/tenant-model-config";
import { mediaApi } from "@/api/models";
import { PooledAccountsSection } from "./ProviderAccountPool";
import { BrandIcon, BRAND_ICONS } from "@/components/brand-icon";
import {
  X, Bot, Key, CheckCircle, XCircle, Loader2,
  Upload, Globe, Search, Image as ImageIcon,
} from "lucide-react";

interface Props {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
}

// --- Provider definitions ---

interface ProviderDef {
  id: string;
  name: string;
  icon: typeof Bot;
  color: string;
  supportsOAuth: boolean;
  oauthLabel?: string;
  apiKeyPlaceholder: string;
  hasEndpoint?: boolean;
  endpointPlaceholder?: string;
}

const PROVIDERS: ProviderDef[] = [
  {
    id: "anthropic",
    name: "Anthropic (Claude)",
    icon: Bot,
    color: "text-orange-500",
    supportsOAuth: true,
    oauthLabel: "Claude Subscription",
    apiKeyPlaceholder: "sk-ant-api03-...",
  },
  {
    id: "openai",
    name: "OpenAI (ChatGPT)",
    icon: Bot,
    color: "text-green-500",
    supportsOAuth: true,
    oauthLabel: "ChatGPT Plus/Team",
    apiKeyPlaceholder: "sk-proj-...",
  },
  {
    id: "azure_foundry",
    name: "Azure AI Foundry",
    icon: Globe,
    color: "text-blue-500",
    supportsOAuth: false,
    apiKeyPlaceholder: "API key",
    hasEndpoint: true,
    endpointPlaceholder: "https://your-resource.services.ai.azure.com",
  },
  {
    id: "google",
    name: "Google (Gemini)",
    icon: Globe,
    color: "text-yellow-500",
    supportsOAuth: false,
    apiKeyPlaceholder: "AIza...",
  },
  {
    id: "groq",
    name: "Groq",
    icon: Globe,
    color: "text-purple-500",
    supportsOAuth: false,
    apiKeyPlaceholder: "gsk_...",
  },
];

const API_KEY_FIELDS: Record<string, string> = {
  anthropic: "anthropicApiKey",
  openai: "openaiApiKey",
  google: "googleApiKey",
  groq: "groqApiKey",
  azure_foundry: "azureFoundryApiKey",
};

export function TenantModelSettings({ tenantId, tenantName, onClose }: Props) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["tenant-model-config", tenantId] });

  const { data: config, isLoading } = useQuery({
    queryKey: ["tenant-model-config", tenantId],
    queryFn: () => tenantModelConfigApi.get(tenantId),
  });

  // --- Mutations ---
  const saveMutation = useMutation({
    mutationFn: (data: Record<string, string>) => tenantModelConfigApi.update(tenantId, data),
    onSuccess: invalidate,
  });

  const removeKeyMutation = useMutation({
    mutationFn: (provider: string) => tenantModelConfigApi.removeProviderKey(tenantId, provider),
    onSuccess: invalidate,
  });

  const syncMutation = useMutation({
    mutationFn: () => tenantModelConfigApi.sync(tenantId),
  });

  // OAuth connect/disconnect lives in PooledAccountsSection (per provider
  // card) — the account pool is the single OAuth surface now.

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 size={24} className="animate-spin text-text-muted" />
      </div>
    );
  }

  const providers = config?.providers || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">LLM Providers</h2>
          <p className="text-sm text-text-muted">{tenantName} — Configure model access for agents</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          <X size={16} />
        </Button>
      </div>

      {/* Provider cards — OAuth (pooled accounts) + direct API key per provider */}
      {PROVIDERS.map((provider) => (
        <ProviderCard
          key={provider.id}
          tenantId={tenantId}
          provider={provider}
          status={providers[provider.id]}
          config={config}
          onSaveKey={(key, endpoint) => {
            const data: Record<string, string> = { [API_KEY_FIELDS[provider.id]]: key };
            if (endpoint && provider.hasEndpoint) data.azureFoundryEndpoint = endpoint;
            saveMutation.mutate(data);
          }}
          onRemoveKey={() => removeKeyMutation.mutate(provider.id)}
          isSaving={saveMutation.isPending}
          isRemoving={removeKeyMutation.isPending}
        />
      ))}

      {/* Default image model for this tenant */}
      <ImageModelCard
        tenantId={tenantId}
        config={config ?? null}
        onSave={(data) => saveMutation.mutate(data)}
        isSaving={saveMutation.isPending}
      />

      {/* Web Search Config */}
      <WebSearchCard
        config={config ?? null}
        onSave={(data) => saveMutation.mutate(data)}
        isSaving={saveMutation.isPending}
      />

      {/* Sync to Agents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload size={18} />
            Sync to Agents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-muted mb-3">
            Push the current LLM provider credentials to all active agents for this tenant.
            OAuth tokens and API keys will be written to agent auth profiles.
          </p>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Upload size={14} className="mr-1.5" />
              )}
              Sync to All Agents
            </Button>
            {syncMutation.data && (
              <Badge variant={syncMutation.data.synced > 0 ? "success" : "danger"}>
                {syncMutation.data.synced}/{syncMutation.data.results.length} agents synced
              </Badge>
            )}
          </div>
          {syncMutation.data && syncMutation.data.results.length > 0 && (
            <div className="mt-3 space-y-1">
              {syncMutation.data.results.map((r) => (
                <div key={r.agentId} className="flex items-center gap-2 text-xs">
                  {r.success ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
                  <span className="text-text-primary">{r.agentName}</span>
                  {r.error && <span className="text-text-muted">- {r.error}</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Web Search Card ---

/**
 * Tenant default image model — what agents on this tenant use for
 * `generate_image` unless an individual agent overrides it in its own settings.
 *
 * Only image models already GRANTED to this tenant appear here; granting one is
 * a platform-admin action in Admin → Model Catalogue.
 */
function ImageModelCard({
  tenantId,
  config,
  onSave,
  isSaving,
}: {
  tenantId: string;
  config: import("@/api/tenant-model-config").TenantModelConfig | null;
  onSave: (data: Record<string, string>) => void;
  isSaving: boolean;
}) {
  const { data } = useQuery({
    queryKey: ["image-models", tenantId],
    queryFn: () => mediaApi.imageModels(tenantId),
  });
  const models = data?.models || [];
  const current = config?.defaultImageModel || "";
  const [selected, setSelected] = useState(current);
  useEffect(() => setSelected(current), [current]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ImageIcon size={18} />
          Image Generation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {models.length === 0 ? (
          <p className="text-sm text-text-muted">
            No image models are available to this tenant. Grant one in Admin → Model Catalogue —
            import it from an Azure Foundry connection first if it isn&apos;t listed.
          </p>
        ) : (
          <>
            <p className="text-sm text-text-muted">
              Default model for the <span className="font-mono text-xs">generate_image</span> tool.
              Individual agents can override this in their own settings.
            </p>
            <div className="flex items-center gap-2">
              <select
                className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="">— None —</option>
                {models.map((m) => (
                  <option key={m.id} value={m.modelId}>
                    {m.displayName}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={selected === current || isSaving}
                onClick={() => onSave({ defaultImageModel: selected })}
              >
                {isSaving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
              </Button>
            </div>
            {!selected && models.length === 1 && (
              <p className="text-[10px] text-text-muted">
                With one image model granted, agents will use it even without a default set.
              </p>
            )}
            {!selected && models.length > 1 && (
              <p className="text-[10px] text-amber-600">
                No default set and more than one model granted — agents without their own choice
                will report that no image model is configured.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const WEB_SEARCH_LABELS: Record<string, string> = {
  duckduckgo: "DuckDuckGo (no key needed)",
  brave: "Brave Search (platform key)",
  perplexity: "Perplexity AI Search",
};

function WebSearchCard({
  config,
  onSave,
  isSaving,
}: {
  config: import("@/api/tenant-model-config").TenantModelConfig | null;
  onSave: (data: Record<string, string>) => void;
  isSaving: boolean;
}) {
  // Only providers with a key on file are selectable. DuckDuckGo needs no key,
  // so it is always available and is what agents fall back to.
  const available = config?.availableWebSearchProviders ?? ["duckduckgo"];
  const effective = config?.effectiveWebSearchProvider ?? "duckduckgo";
  const stored = config?.webSearchProvider ?? null;
  const [provider, setProvider] = useState<string>(effective);
  const [perplexityKey, setPerplexityKey] = useState("");
  const hasPerplexityKey = config?.hasPerplexityApiKey || false;

  // A stored choice whose key was removed no longer applies.
  const droppedChoice = stored !== null && !(available as string[]).includes(stored);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Search size={18} />
          Web Search
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-text-muted">
          Choose the search engine agents use. Only providers with an API key on file are
          listed. DuckDuckGo needs no key and is always available.
        </p>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-text-muted">Search Provider</label>
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                onSave({ webSearchProvider: e.target.value });
              }}
            >
              {available.map((p) => (
                <option key={p} value={p}>
                  {WEB_SEARCH_LABELS[p]}
                </option>
              ))}
            </select>
            {available.length === 1 && (
              <p className="mt-1 text-xs text-text-muted">
                Add a Brave key in Platform Settings, or a Perplexity key below, to unlock
                more options.
              </p>
            )}
            {droppedChoice && (
              <p className="mt-1 text-xs text-amber-500">
                {stored} has no key on file, so agents are using{" "}
                {WEB_SEARCH_LABELS[effective]}.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border p-3 space-y-2">
            <span className="text-xs font-medium text-text-secondary">
              <Key size={12} className="inline mr-1" />
              Perplexity API Key
            </span>
            {hasPerplexityKey ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-surface-hover px-2 py-1 text-xs font-mono text-text-muted">
                  {config?.perplexityApiKey || "****"}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500"
                  onClick={() => onSave({ perplexityApiKey: "" })}
                >
                  <XCircle size={12} className="mr-1" />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  placeholder="pplx-..."
                  value={perplexityKey}
                  onChange={(e) => setPerplexityKey(e.target.value)}
                  className="flex-1 text-xs"
                />
                <Button
                  size="sm"
                  disabled={!perplexityKey.trim() || isSaving}
                  onClick={() => {
                    onSave({ perplexityApiKey: perplexityKey });
                    setPerplexityKey("");
                  }}
                >
                  {isSaving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Provider Row Card ---

function ProviderCard({
  tenantId,
  provider,
  status,
  config,
  onSaveKey,
  onRemoveKey,
  isSaving,
  isRemoving,
}: {
  tenantId: string;
  provider: ProviderDef;
  status?: ProviderStatus;
  config: import("@/api/tenant-model-config").TenantModelConfig | null | undefined;
  onSaveKey: (key: string, endpoint?: string) => void;
  onRemoveKey: () => void;
  isSaving: boolean;
  isRemoving: boolean;
}) {
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const Icon = provider.icon;
  const brand = BRAND_ICONS[provider.id];

  const isReady = status?.ready || false;
  const method = status?.method || "none";
  const cfg = config as Record<string, unknown> | null | undefined;
  const hasApiKeyMap: Record<string, string> = {
    anthropic: "hasAnthropicApiKey",
    openai: "hasOpenaiApiKey",
    google: "hasGoogleApiKey",
    groq: "hasGroqApiKey",
    azure_foundry: "hasAzureFoundryApiKey",
  };
  const hasApiKey = !!(cfg?.[hasApiKeyMap[provider.id]]);
  const maskedKey = (cfg?.[API_KEY_FIELDS[provider.id]] as string | null) ?? null;

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {brand ? (
              <BrandIcon brand={brand} size={18} className={brand.hex ? undefined : "text-text-primary"} />
            ) : (
              <Icon size={18} className={provider.color} />
            )}
            <span className="text-sm font-semibold text-text-primary">{provider.name}</span>
          </div>
          <Badge variant={isReady ? "success" : "default"} className="gap-1">
            {isReady ? <CheckCircle size={10} /> : <XCircle size={10} />}
            {status?.label || "Not configured"}
          </Badge>
        </div>

        <div className="space-y-3">
          {/* OAuth — pooled subscription accounts (one surface, N accounts,
              load-balanced across agents). */}
          {provider.supportsOAuth && (
            <PooledAccountsSection
              tenantId={tenantId}
              provider={provider.id === "anthropic" ? "anthropic" : "openai-codex"}
            />
          )}

          {/* API Key section */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <span className="text-xs font-medium text-text-secondary">
              <Key size={12} className="inline mr-1" />
              API Key (Direct)
            </span>
            {hasApiKey ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-surface-hover px-2 py-1 text-xs font-mono text-text-muted">
                  {maskedKey || "****"}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500"
                  disabled={isRemoving}
                  onClick={onRemoveKey}
                >
                  <XCircle size={12} className="mr-1" />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    placeholder={provider.apiKeyPlaceholder}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="flex-1 text-xs"
                  />
                  <Button
                    size="sm"
                    disabled={!apiKey.trim() || isSaving}
                    onClick={() => { onSaveKey(apiKey, endpoint); setApiKey(""); setEndpoint(""); }}
                  >
                    {isSaving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
                  </Button>
                </div>
                {provider.hasEndpoint && (
                  <Input
                    placeholder={provider.endpointPlaceholder}
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    className="text-xs"
                  />
                )}
              </div>
            )}
            {method === "platform_vault" && !hasApiKey && (
              <p className="text-[10px] text-amber-600">
                Using shared platform key. Add a tenant-specific key for isolation.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
